import { apiRequest } from '../../../shared/services/apiClient';
import {
  ChannelAccount,
  ChannelAccountChannel,
  ChannelAccountCredentialStatus,
  ChannelAccountListResponse,
  CreateInstagramChannelAccountRequest,
  CreateWhatsAppChannelAccountRequest,
  UpdateChannelAccountRequest,
  UpdateInstagramChannelAccountCredentialsRequest,
  UpdateWhatsAppChannelAccountCredentialsRequest,
  ChannelAccountLimitsSettingsResponse,
  UpdateChannelAccountLimitsRequest,
  ChannelAccountWhatsAppSettings,
  ChannelAccountInstagramSettings,
  ChannelAccountTelegramSettings,
  ChannelAccountMessengerSettings,
  ChannelAccountTikTokSettings,
  ChannelAccountVoiceSettings,
  CreateTelegramChannelAccountRequest,
  UpdateTelegramChannelAccountCredentialsRequest,
  CreateMessengerChannelAccountRequest,
  UpdateMessengerChannelAccountCredentialsRequest,
  CreateTikTokChannelAccountRequest,
  UpdateTikTokChannelAccountCredentialsRequest,
  CreateVoiceChannelAccountRequest,
  UpdateVoiceChannelAccountCredentialsRequest,
  ChannelAccountSmsSettings,
  CreateSmsChannelAccountRequest,
  UpdateSmsChannelAccountCredentialsRequest,
  CallingConnectionTestResult,
  WhatsAppAppSubscription,
  WhatsAppPhoneNumberRegistration,
  WhatsAppSubscribedApp,
  WhatsAppSubscribedApps,
} from '../types/channelAccount';

const pickSingle = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const envelope = payload as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === 'object') {
    return envelope.data;
  }

  return payload;
};

const normalizeCredentialStatus = (value: unknown): ChannelAccountCredentialStatus | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const payload = value as Record<string, unknown>;
  return {
    hasAccessToken: Boolean(payload.hasAccessToken),
    hasWebhookVerifyToken: Boolean(payload.hasWebhookVerifyToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignatureEnabled: Boolean(payload.validateSignatureEnabled),
  };
};

const normalizeAccount = (value: unknown): ChannelAccount => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    id: String(payload.id ?? ''),
    channel: String(payload.channel ?? 'WhatsApp') as ChannelAccountChannel,
    externalAccountId: String(payload.externalAccountId ?? ''),
    displayName: String(payload.displayName ?? ''),
    isEnabled: Boolean(payload.isEnabled),
    isDefault: Boolean(payload.isDefault),
    createdAtUtc: String(payload.createdAtUtc ?? ''),
    updatedAtUtc: String(payload.updatedAtUtc ?? ''),
    credentialStatus: normalizeCredentialStatus(payload.credentialStatus),
  };
};

const normalizeListResponse = (value: unknown): ChannelAccountListResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const limitsRaw = payload.limits && typeof payload.limits === 'object'
    ? payload.limits as Record<string, unknown>
    : {};
  const itemsRaw = Array.isArray(payload.items) ? payload.items : [];

  return {
    items: itemsRaw.map(normalizeAccount),
    limits: {
      maxWhatsApp: Number(limitsRaw.maxWhatsApp ?? 1),
      maxInstagram: Number(limitsRaw.maxInstagram ?? 1),
      maxWebchat: Number(limitsRaw.maxWebchat ?? 5),
      maxTelegram: Number(limitsRaw.maxTelegram ?? 1),
      maxMessenger: Number(limitsRaw.maxMessenger ?? 1),
      maxTikTok: Number(limitsRaw.maxTikTok ?? 1),
      maxVoice: Number(limitsRaw.maxVoice ?? 1),
      maxTotal: Number(limitsRaw.maxTotal ?? 10),
      usedWhatsApp: Number(limitsRaw.usedWhatsApp ?? 0),
      usedInstagram: Number(limitsRaw.usedInstagram ?? 0),
      usedWebchat: Number(limitsRaw.usedWebchat ?? 0),
      usedTelegram: Number(limitsRaw.usedTelegram ?? 0),
      usedMessenger: Number(limitsRaw.usedMessenger ?? 0),
      usedTikTok: Number(limitsRaw.usedTikTok ?? 0),
      usedVoice: Number(limitsRaw.usedVoice ?? 0),
      usedTotal: Number(limitsRaw.usedTotal ?? 0),
    },
  };
};

const normalizeWhatsAppSettings = (value: unknown): ChannelAccountWhatsAppSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    cloudApiBaseUrl: String(payload.cloudApiBaseUrl ?? 'https://graph.facebook.com'),
    cloudApiVersion: String(payload.cloudApiVersion ?? 'v25.0'),
    whatsAppAccountId: payload.whatsAppAccountId ? String(payload.whatsAppAccountId) : null,
    cloudPhoneNumberId: String(payload.cloudPhoneNumberId ?? ''),
    phoneNumber: payload.phoneNumber ? String(payload.phoneNumber) : null,
    hasCloudAccessToken: Boolean(payload.hasCloudAccessToken),
    hasCloudWebhookVerifyToken: Boolean(payload.hasCloudWebhookVerifyToken),
    hasCloudAppSecret: Boolean(payload.hasCloudAppSecret),
    validateCloudSignature: Boolean(payload.validateCloudSignature),
  };
};

const normalizeInstagramAccountSettings = (value: unknown): ChannelAccountInstagramSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://graph.instagram.com'),
    apiVersion: String(payload.apiVersion ?? 'v25.0'),
    instagramBusinessAccountId: String(payload.instagramBusinessAccountId ?? ''),
    instagramUsername: payload.instagramUsername ? String(payload.instagramUsername) : null,
    defaultCommentReplyMode: String(payload.defaultCommentReplyMode ?? 'public_comment'),
    hasPageAccessToken: Boolean(payload.hasPageAccessToken),
  };
};

const normalizeTelegramAccountSettings = (value: unknown): ChannelAccountTelegramSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://api.telegram.org'),
    hasBotToken: Boolean(payload.hasBotToken),
    hasWebhookSecretToken: Boolean(payload.hasWebhookSecretToken),
  };
};

const normalizeMessengerAccountSettings = (value: unknown): ChannelAccountMessengerSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://graph.facebook.com'),
    apiVersion: String(payload.apiVersion ?? 'v25.0'),
    pageId: String(payload.pageId ?? ''),
    hasPageAccessToken: Boolean(payload.hasPageAccessToken),
    hasWebhookVerifyToken: Boolean(payload.hasWebhookVerifyToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignature: Boolean(payload.validateSignature),
  };
};

const normalizeTikTokAccountSettings = (value: unknown): ChannelAccountTikTokSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://business-api.tiktok.com/open_api/v1.3'),
    businessOpenId: String(payload.businessOpenId ?? ''),
    appId: payload.appId ? String(payload.appId) : null,
    hasAccessToken: Boolean(payload.hasAccessToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignature: Boolean(payload.validateSignature),
  };
};

export const getWhatsAppChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountWhatsAppSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/whatsapp-settings`));
  return normalizeWhatsAppSettings(payload);
};

export const getInstagramChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountInstagramSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/instagram-settings`));
  return normalizeInstagramAccountSettings(payload);
};

export const getTelegramChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountTelegramSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/telegram-settings`));
  return normalizeTelegramAccountSettings(payload);
};

export const getMessengerChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountMessengerSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/messenger-settings`));
  return normalizeMessengerAccountSettings(payload);
};

export const getTikTokChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountTikTokSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/tiktok-settings`));
  return normalizeTikTokAccountSettings(payload);
};

export const listChannelAccounts = async (
  channel?: ChannelAccountChannel,
): Promise<ChannelAccountListResponse> => {
  const query = channel ? `?channel=${encodeURIComponent(channel)}` : '';
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts${query}`));
  return normalizeListResponse(payload);
};

const normalizeLimits = (value: unknown) => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    maxWhatsApp: Number(payload.maxWhatsApp ?? 1),
    maxInstagram: Number(payload.maxInstagram ?? 1),
    maxWebchat: Number(payload.maxWebchat ?? 5),
    maxTelegram: Number(payload.maxTelegram ?? 1),
    maxMessenger: Number(payload.maxMessenger ?? 1),
    maxTikTok: Number(payload.maxTikTok ?? 1),
    maxVoice: Number(payload.maxVoice ?? 1),
    maxTotal: Number(payload.maxTotal ?? 10),
    usedWhatsApp: Number(payload.usedWhatsApp ?? 0),
    usedInstagram: Number(payload.usedInstagram ?? 0),
    usedWebchat: Number(payload.usedWebchat ?? 0),
    usedTelegram: Number(payload.usedTelegram ?? 0),
    usedMessenger: Number(payload.usedMessenger ?? 0),
    usedTikTok: Number(payload.usedTikTok ?? 0),
    usedVoice: Number(payload.usedVoice ?? 0),
    usedTotal: Number(payload.usedTotal ?? 0),
  };
};

const normalizeLimitsSettingsResponse = (value: unknown): ChannelAccountLimitsSettingsResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    effective: normalizeLimits(payload.effective),
    defaults: normalizeLimits(payload.defaults),
    hasOverrides: Boolean(payload.hasOverrides),
  };
};

export const getChannelAccountLimitsSettings = async (): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits'));
  return normalizeLimitsSettingsResponse(payload);
};

export const updateChannelAccountLimits = async (
  request: UpdateChannelAccountLimitsRequest,
): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits', {
    method: 'PUT',
    body: JSON.stringify(request),
  }));
  return normalizeLimitsSettingsResponse(payload);
};

export const resetChannelAccountLimits = async (): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits', {
    method: 'DELETE',
  }));
  return normalizeLimitsSettingsResponse(payload);
};

export const updateChannelAccount = async (
  channelAccountId: string,
  request: UpdateChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const sendWhatsAppChannelAccountTestMessage = async (
  channelAccountId: string,
  request: {
    to: string;
    body: string;
    type?: 'text';
    previewUrl?: boolean;
  },
): Promise<{ messageSid: string; to: string; status: string; sentAt: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/test-send`,
    {
      method: 'POST',
      body: JSON.stringify({
        type: request.type ?? 'text',
        to: request.to,
        body: request.body,
        ...(request.previewUrl !== undefined ? { previewUrl: request.previewUrl } : {}),
      }),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    messageSid: String(result.messageSid ?? ''),
    to: String(result.to ?? request.to),
    status: String(result.status ?? ''),
    sentAt: String(result.sentAt ?? ''),
  };
};

export const sendInstagramChannelAccountTestMessage = async (
  channelAccountId: string,
  request: {
    recipientId?: string;
    commentId?: string;
    text: string;
  },
): Promise<{ recipientId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/instagram/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    recipientId: String(result.recipientId ?? request.recipientId ?? ''),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

export const createWhatsAppChannelAccount = async (
  request: CreateWhatsAppChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/whatsapp', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateWhatsAppChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateWhatsAppChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

const normalizeWhatsAppAppSubscription = (value: unknown): WhatsAppAppSubscription => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    whatsAppAccountId: String(payload.whatsAppAccountId ?? ''),
    success: Boolean(payload.success),
    updatedAtUtc: String(payload.updatedAtUtc ?? ''),
  };
};

const normalizeWhatsAppSubscribedApp = (value: unknown): WhatsAppSubscribedApp => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    appId: payload.appId ? String(payload.appId) : null,
    appName: payload.appName ? String(payload.appName) : null,
    appLink: payload.appLink ? String(payload.appLink) : null,
  };
};

export const registerWhatsAppPhoneNumber = async (
  channelAccountId: string,
  request: { pin: string; dataLocalizationRegion?: string },
): Promise<WhatsAppPhoneNumberRegistration> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/register-number`,
    {
      method: 'POST',
      body: JSON.stringify({
        pin: request.pin,
        ...(request.dataLocalizationRegion ? { dataLocalizationRegion: request.dataLocalizationRegion } : {}),
      }),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    cloudPhoneNumberId: String(result.cloudPhoneNumberId ?? ''),
    success: Boolean(result.success),
    registeredAtUtc: String(result.registeredAtUtc ?? ''),
  };
};

export const subscribeWhatsAppApp = async (
  channelAccountId: string,
): Promise<WhatsAppAppSubscription> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribe-app`,
    { method: 'POST' },
  ));
  return normalizeWhatsAppAppSubscription(payload);
};

export const unsubscribeWhatsAppApp = async (
  channelAccountId: string,
): Promise<WhatsAppAppSubscription> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribed-apps`,
    { method: 'DELETE' },
  ));
  return normalizeWhatsAppAppSubscription(payload);
};

export const getWhatsAppSubscribedApps = async (
  channelAccountId: string,
): Promise<WhatsAppSubscribedApps> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribed-apps`,
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const itemsRaw = Array.isArray(result.items) ? result.items : [];
  return {
    whatsAppAccountId: String(result.whatsAppAccountId ?? ''),
    items: itemsRaw.map(normalizeWhatsAppSubscribedApp),
    count: Number(result.count ?? itemsRaw.length),
    retrievedAtUtc: String(result.retrievedAtUtc ?? ''),
  };
};

export const createInstagramChannelAccount = async (
  request: CreateInstagramChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/instagram', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateInstagramChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateInstagramChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/instagram-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const createTelegramChannelAccount = async (
  request: CreateTelegramChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/telegram', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateTelegramChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateTelegramChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/telegram-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendTelegramChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { chatId: string; text: string },
): Promise<{ chatId: string; messageId: string; sentAt: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/telegram/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    chatId: String(result.chatId ?? request.chatId),
    messageId: String(result.messageId ?? ''),
    sentAt: String(result.sentAt ?? ''),
  };
};

export const createMessengerChannelAccount = async (
  request: CreateMessengerChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/messenger', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateMessengerChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateMessengerChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/messenger-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendMessengerChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { recipientId: string; text: string },
): Promise<{ recipientId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/messenger/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    recipientId: String(result.recipientId ?? request.recipientId),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

export const createTikTokChannelAccount = async (
  request: CreateTikTokChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/tiktok', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateTikTokChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateTikTokChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/tiktok-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendTikTokChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { conversationId: string; text: string },
): Promise<{ conversationId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/tiktok/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    conversationId: String(result.conversationId ?? request.conversationId),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

const normalizeVoiceAccountSettings = (value: unknown): ChannelAccountVoiceSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    serverUrl: String(payload.serverUrl ?? ''),
    hasClientId: Boolean(payload.hasClientId),
    hasClientSecret: Boolean(payload.hasClientSecret),
    inboundEnabled: payload.inboundEnabled !== false,
    outboundEnabled: payload.outboundEnabled !== false,
    defaultCallerId: payload.defaultCallerId ? String(payload.defaultCallerId) : null,
    provider: String(payload.provider ?? 'ThreeCx'),
  };
};

export const getVoiceChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountVoiceSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/voice-settings`));
  return normalizeVoiceAccountSettings(payload);
};

export const createVoiceChannelAccount = async (
  request: CreateVoiceChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/voice', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateVoiceChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateVoiceChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/voice-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const testVoiceChannelAccountConnection = async (
  channelAccountId: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/voice/test-connection`,
    { method: 'POST' },
  ));
  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    succeeded: Boolean(result.succeeded),
    message: result.message ? String(result.message) : null,
    testedAtUtc: String(result.testedAtUtc ?? ''),
  };
};

const normalizeSmsAccountSettings = (value: unknown): ChannelAccountSmsSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiUrl: String(payload.apiUrl ?? ''),
    httpMethod: String(payload.httpMethod ?? 'POST'),
    contentType: String(payload.contentType ?? 'application/json'),
    bodyTemplate: payload.bodyTemplate ? String(payload.bodyTemplate) : null,
    authHeaderName: payload.authHeaderName ? String(payload.authHeaderName) : null,
    hasAuthHeaderValue: Boolean(payload.hasAuthHeaderValue),
    basicAuthUsername: payload.basicAuthUsername ? String(payload.basicAuthUsername) : null,
    hasBasicAuthPassword: Boolean(payload.hasBasicAuthPassword),
    senderName: payload.senderName ? String(payload.senderName) : null,
    successContains: payload.successContains ? String(payload.successContains) : null,
    outboundEnabled: payload.outboundEnabled !== false,
  };
};

export const getSmsChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountSmsSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/sms-settings`));
  return normalizeSmsAccountSettings(payload);
};

export const createSmsChannelAccount = async (
  request: CreateSmsChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/sms', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateSmsChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateSmsChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/sms-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const testSmsChannelAccountConnection = async (
  channelAccountId: string,
  phoneNumber: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/sms/test-connection?phoneNumber=${encodeURIComponent(phoneNumber)}`,
    { method: 'POST' },
  ));
  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    succeeded: Boolean(result.succeeded),
    message: result.message ? String(result.message) : null,
    testedAtUtc: String(result.testedAtUtc ?? ''),
  };
};
